import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Phase TC-2 — guest-side public API client.
 *
 * Deliberately NOT using the back-office ApiService:
 *   - no auth interceptor should run on a public token URL;
 *   - error envelope shape differs (we want a clean discriminated
 *     union, not the back-office { success, error } wrapper).
 *
 * Both calls share a uniform "not available" failure mode so the UI
 * cannot accidentally distinguish between "wrong token", "revoked",
 * "inactive store", or "inactive table" — anti-enumeration.
 */

// M2 DTO split — the DTOs / interfaces / type aliases that used to
// live in this file have moved to ./guest-landing-dtos so the
// service file holds just the HTTP client class. Re-exported here
// so existing consumer imports like
//   import { PublicTableAccessResponse } from '...guest-landing.service'
// continue to compile without edits.
import {
  PublicTableAccessResponse,
  PublicActiveBillResponse,
  PublicMenuCategory,
  PublicMenuItemSummary,
  PublicMenuItemDetail,
  GuestApiResult,
  CreatePaymentIntentRequest,
  CreatePaymentIntentResponse,
  PaymentIntentStatusResponse,
  DigitalReceiptResponse,
  CreatePaymentIntentResult,
  RequestBillResult,
  GuestTipRequest,
  GuestTipResult,
  PickupOrderStatusResponse,
  CreatePickupPaymentIntentRequest,
  CreateSessionPaymentIntentRequest,
  SubmitGuestOrderRequest,
  SubmitOrderResult,
  SubmitGuestOrderResponse,
  AddGuestCartLineRequest,
  UpdateGuestCartLineRequest,
  SubmitGuestSessionRequest,
  GuestSessionSnapshot,
  GuestSessionApiResult,
  PostGuestChatRequest,
  GuestChatTurnResponse,
  GuestChatApiResult,
} from './guest-landing-dtos';
export * from './guest-landing-dtos';

/**
 * OMNI-2a — G-8: the guest menu reads (categories, departments, items, item
 * detail) take an optional fulfilment. Only `'delivery'` changes the request:
 * it appends `fulfilment=delivery`, which the server resolves to the
 * GuestDelivery sales-channel surface on a pickup token. `null` / omitted
 * leaves the URL byte-identical, so dine-in, pickup and kiosk reads (and their
 * `Cache-Control: public` CDN entries) are untouched.
 *
 * Fix round 1 (F3) — callers (the menu screen, the cuisine wall) only pass
 * `'delivery'` when the fulfilment config says `channelAvailabilityEnabled`;
 * for every other tenant these reads are exactly the pre-OMNI requests.
 */
export type GuestMenuFulfilment = 'delivery' | null;

/** OMNI-2a — append `fulfilment=delivery` to a guest menu URL when asked (see {@link GuestMenuFulfilment}). */
export function withGuestMenuFulfilment(url: string, fulfilment: GuestMenuFulfilment | undefined): string {
  if (fulfilment !== 'delivery') return url;
  return `${url}${url.includes('?') ? '&' : '?'}fulfilment=delivery`;
}

@Injectable({ providedIn: 'root' })
export class GuestLandingService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  resolveToken(publicToken: string): Observable<GuestApiResult<PublicTableAccessResponse>> {
    return this.http
      .get<{ success: boolean; data?: PublicTableAccessResponse; error?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}`)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestApiResult<PublicTableAccessResponse>)
          : ({ ok: false, kind: 'not-available' } as GuestApiResult<PublicTableAccessResponse>)),
        catchError((err: HttpErrorResponse) => of(this.classify<PublicTableAccessResponse>(err))));
  }

  resolveActiveBill(publicToken: string): Observable<GuestApiResult<PublicActiveBillResponse>> {
    return this.http
      .get<{ success: boolean; data?: PublicActiveBillResponse; error?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/active-bill`)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestApiResult<PublicActiveBillResponse>)
          : ({ ok: false, kind: 'not-available' } as GuestApiResult<PublicActiveBillResponse>)),
        catchError((err: HttpErrorResponse) => of(this.classify<PublicActiveBillResponse>(err))));
  }

  /**
   * TC-GBR — ask the store to prepare the bill, guest-side.
   *
   * Only offered when the resolve said `guestBillRequestEnabled`; the
   * server re-checks every precondition and 403s otherwise. The
   * response IS the refreshed active bill (now Billed, with service
   * charges / auto-promotions applied), so the caller does not need a
   * second `resolveActiveBill` hop. A second tap — or a POS user
   * billing concurrently — settles as a success with the current bill,
   * never an error the guest can't act on.
   */
  requestBill(publicToken: string): Observable<RequestBillResult> {
    return this.http
      .post<{ success: boolean; data?: PublicActiveBillResponse; error?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/request-bill`,
        {})
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as RequestBillResult)
          : ({ ok: false, kind: 'unknown',
               message: res?.error ?? 'Please ask the restaurant team to prepare your bill.' } as RequestBillResult)),
        catchError((err: HttpErrorResponse) => {
          // 403 = the store switched the setting off between page load
          // and tap. 409 = held lines / no active bill. Both mean "go
          // back to the staff gate", so both get an actionable sentence
          // rather than the raw body.
          if (err.status === 403)
            return of({ ok: false, kind: 'not-allowed',
              message: err.error?.error ?? 'Please ask the restaurant team to prepare your bill.' } as RequestBillResult);
          if (err.status === 409)
            return of({ ok: false, kind: 'conflict',
              message: err.error?.error ?? 'Please ask the restaurant team to prepare your bill.' } as RequestBillResult);
          if (err.status === 404)
            return of({ ok: false, kind: 'not-available',
              message: 'This QR is no longer available.' } as RequestBillResult);
          if (err.status >= 400 && err.status < 500)
            return of({ ok: false, kind: 'unknown',
              message: err.error?.error ?? 'Please ask the restaurant team to prepare your bill.' } as RequestBillResult);
          return of({ ok: false, kind: 'network',
            message: 'Network error. Please try again.' } as RequestBillResult);
        }));
  }

  /**
   * TCTIP-1 — add (or replace, or with amount 0 remove) the guest's tip
   * on a Billed check.
   *
   * Only offered when the resolve said `tipEnabled`; the server
   * re-checks every precondition and 403s otherwise. The body carries
   * exactly one of percent / amount plus the bill's checkVersion, and
   * the response IS the refreshed active bill (tipTotal, grandTotal and
   * version all moved), so the caller assigns it and the Pay button
   * follows. Error mapping mirrors {@link createPaymentIntent} for the
   * 409 version_mismatch code and {@link requestBill} for the rest.
   */
  applyTip(publicToken: string, req: GuestTipRequest): Observable<GuestTipResult> {
    return this.http
      .post<{ success: boolean; data?: PublicActiveBillResponse; error?: string; code?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/tip`,
        req)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestTipResult)
          : ({ ok: false, kind: 'unknown',
               message: res?.error ?? 'Could not add your tip. Please try again.' } as GuestTipResult)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 409) {
            const code = (err.error?.code as string) ?? '';
            // Same lock as the payment intent: the bill moved under the
            // guest, so the tip they chose was for a bill that no longer
            // exists. The page refreshes and lets them choose again.
            if (code === 'version_mismatch')
              return of({ ok: false, kind: 'version-mismatch',
                message: err.error?.error ?? 'Your bill changed. Please refresh before paying.' } as GuestTipResult);
            // 409 without the version code: no active bill, check not
            // Billed any more, or more than one check on the table. The
            // backend's sentence is already guest-actionable.
            return of({ ok: false, kind: 'conflict',
              message: err.error?.error ?? 'Please ask the restaurant team for assistance.' } as GuestTipResult);
          }
          if (err.status === 403)
            return of({ ok: false, kind: 'not-allowed',
              message: err.error?.error ?? 'Tips are not available right now.' } as GuestTipResult);
          // 422 = the tip is larger than the bill (the client never sends
          // a body the server's shape validation would refuse).
          if (err.status === 422)
            return of({ ok: false, kind: 'too-large',
              message: err.error?.error ?? 'That tip is larger than the bill.' } as GuestTipResult);
          if (err.status === 429)
            return of({ ok: false, kind: 'rate-limited',
              message: 'Please wait a moment before changing your tip.' } as GuestTipResult);
          if (err.status === 404)
            return of({ ok: false, kind: 'not-available',
              message: 'This QR is no longer available.' } as GuestTipResult);
          if (err.status >= 400 && err.status < 500)
            return of({ ok: false, kind: 'unknown',
              message: err.error?.error ?? 'Could not add your tip. Please try again.' } as GuestTipResult);
          return of({ ok: false, kind: 'network',
            message: 'Network error. Please try again.' } as GuestTipResult);
        }));
  }

  // ── Phase TC-3 — menu read paths ──

  resolveMenuCategories(
    publicToken: string, fulfilment: GuestMenuFulfilment = null,
  ): Observable<GuestApiResult<PublicMenuCategory[]>> {
    return this.http
      .get<{ success: boolean; data?: PublicMenuCategory[]; error?: string }>(withGuestMenuFulfilment(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/menu/categories`, fulfilment))
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestApiResult<PublicMenuCategory[]>)
          : ({ ok: false, kind: 'not-available' } as GuestApiResult<PublicMenuCategory[]>)),
        catchError((err: HttpErrorResponse) => of(this.classify<PublicMenuCategory[]>(err))));
  }

  /**
   * TC-MS1 — <c>categoryId</c> is now optional. When null/undefined the
   * backend returns items across all active categories for the token's
   * store. Used by the in-menu search field.
   *
   * OMNI-2a — `fulfilment`: see {@link GuestMenuFulfilment}.
   */
  resolveMenuItems(
    publicToken: string, categoryId: string | null = null, fulfilment: GuestMenuFulfilment = null,
  ): Observable<GuestApiResult<PublicMenuItemSummary[]>> {
    const qs = categoryId ? `?categoryId=${encodeURIComponent(categoryId)}` : '';
    const url = withGuestMenuFulfilment(
      `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/menu/items${qs}`, fulfilment);
    return this.http.get<{ success: boolean; data?: PublicMenuItemSummary[]; error?: string }>(url)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestApiResult<PublicMenuItemSummary[]>)
          : ({ ok: false, kind: 'not-available' } as GuestApiResult<PublicMenuItemSummary[]>)),
        catchError((err: HttpErrorResponse) => of(this.classify<PublicMenuItemSummary[]>(err))));
  }

  /** OMNI-2a — a detail denied on the guest's channel is a 404 → `not-available`, like any gone item. */
  resolveMenuItemDetail(
    publicToken: string, itemId: string, fulfilment: GuestMenuFulfilment = null,
  ): Observable<GuestApiResult<PublicMenuItemDetail>> {
    const url = withGuestMenuFulfilment(
      `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/menu/items/${encodeURIComponent(itemId)}`,
      fulfilment);
    return this.http.get<{ success: boolean; data?: PublicMenuItemDetail; error?: string }>(url)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestApiResult<PublicMenuItemDetail>)
          : ({ ok: false, kind: 'not-available' } as GuestApiResult<PublicMenuItemDetail>)),
        catchError((err: HttpErrorResponse) => of(this.classify<PublicMenuItemDetail>(err))));
  }

  /**
   * Phase TC-4 — submit the guest cart. Returns a discriminated
   * union so the cart UI can branch on success / items-unavailable
   * (refresh menu) / fingerprint-mismatch (regenerate key) / rate
   * limit / network. Server is authoritative on price + availability.
   */
  submitOrder(publicToken: string, body: SubmitGuestOrderRequest): Observable<SubmitOrderResult> {
    const url = `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/orders`;
    return this.http.post<any>(url, body).pipe(
      map(res => {
        if (res?.success && res.data) {
          return { ok: true, data: res.data as SubmitGuestOrderResponse } as SubmitOrderResult;
        }
        return { ok: false, kind: 'unknown', message: res?.error ?? 'Could not submit your order.' } as SubmitOrderResult;
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 429) {
          return of({ ok: false, kind: 'rate-limited',
            message: 'Too many submits. Please wait a moment and try again.' } as SubmitOrderResult);
        }
        if (err.status === 409) {
          const code = (err.error && err.error.code) || '';
          if (code === 'items_unavailable') {
            return of({ ok: false, kind: 'items-unavailable',
              message: err.error?.error ?? 'Some items are no longer available. Please refresh the menu.' } as SubmitOrderResult);
          }
          if (code === 'idempotency_fingerprint_mismatch') {
            return of({ ok: false, kind: 'fingerprint-mismatch',
              message: err.error?.error ?? 'Cart changed mid-submit. Please retry.' } as SubmitOrderResult);
          }
          return of({ ok: false, kind: 'conflict', message: err.error?.error ?? 'Conflict.' } as SubmitOrderResult);
        }
        if (err.status === 404) {
          return of({ ok: false, kind: 'not-available',
            message: 'This QR is no longer available.' } as SubmitOrderResult);
        }
        if (err.status >= 400 && err.status < 500) {
          return of({ ok: false, kind: 'validation',
            message: err.error?.error ?? 'Please check your order and try again.' } as SubmitOrderResult);
        }
        return of({ ok: false, kind: 'network', message: 'Network error. Please try again.' } as SubmitOrderResult);
      }));
  }

  private classify<T>(err: HttpErrorResponse): GuestApiResult<T> {
    // 404 / 4xx = "not-available" (uniform anti-enumeration).
    // 0 / 5xx / network errors = "network" so we can show "try again".
    if (err.status >= 400 && err.status < 500) {
      return { ok: false, kind: 'not-available' };
    }
    return { ok: false, kind: 'network' };
  }

  // ── Phase TC-9 — Pay-at-Table ──

  /**
   * Create a Stripe Checkout session for the guest's Billed check.
   * Returns a discriminated result so the bill page can branch on
   * the structured failure codes (version mismatch, pay disabled,
   * gateway error, etc.) without parsing free-form error text.
   */
  createPaymentIntent(
    publicToken: string,
    req: CreatePaymentIntentRequest,
  ): Observable<CreatePaymentIntentResult> {
    return this.http
      .post<{ success: boolean; data?: CreatePaymentIntentResponse; error?: string; code?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/payment-intents`,
        req)
      .pipe(
        map(res => {
          if (res.success && res.data)
            return { ok: true, data: res.data } as CreatePaymentIntentResult;
          return { ok: false, kind: 'unknown', message: res?.error ?? 'Could not start payment.' } as CreatePaymentIntentResult;
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 409) {
            const code = (err.error?.code as string) ?? '';
            if (code === 'version_mismatch') return of({ ok: false, kind: 'version-mismatch',
              message: err.error?.error ?? 'Your bill changed. Please refresh before paying.' } as CreatePaymentIntentResult);
            if (code === 'idempotency_mismatch') return of({ ok: false, kind: 'idempotency-mismatch',
              message: err.error?.error ?? 'Please retry the payment.' } as CreatePaymentIntentResult);
            // 409 without a known code: treat as no-active-bill or open-not-billed
            const text = (err.error?.error ?? '') as string;
            if (text.toLowerCase().includes('prepare your bill'))
              return of({ ok: false, kind: 'open-not-billed', message: text } as CreatePaymentIntentResult);
            return of({ ok: false, kind: 'no-active-bill', message: text } as CreatePaymentIntentResult);
          }
          if (err.status === 403)
            return of({ ok: false, kind: 'pay-not-available',
              // TC-12 — surface the backend's specific resolver
              // reason verbatim ("Online payment gateway is
              // disabled for this store.") instead of a generic copy.
              message: err.error?.error ?? 'Online payment is not available right now.' } as CreatePaymentIntentResult);
          // TC-12 — both 502 (gateway error) and 503 (resolver
          // says misconfigured: missing/wrong key, prefix mismatch,
          // etc.) flow through the same UI branch with the
          // backend's safe error string.
          if (err.status === 502 || err.status === 503)
            return of({ ok: false, kind: 'gateway-error',
              message: err.error?.error ?? 'Payment provider unavailable.' } as CreatePaymentIntentResult);
          if (err.status === 404)
            return of({ ok: false, kind: 'not-available', message: 'This QR is no longer available.' } as CreatePaymentIntentResult);
          if (err.status === 422)
            return of({ ok: false, kind: 'validation',
              message: err.error?.error ?? 'Please try again.' } as CreatePaymentIntentResult);
          if (err.status >= 400 && err.status < 500)
            return of({ ok: false, kind: 'unknown',
              message: err.error?.error ?? 'Please try again.' } as CreatePaymentIntentResult);
          return of({ ok: false, kind: 'network', message: 'Network error. Please try again.' } as CreatePaymentIntentResult);
        }));
  }

  /**
   * Magnati Hosted Checkout completion verification. Called by the bill
   * page from the Lightbox <c>onCompletion</c> callback with the MPGS-issued
   * <c>resultIndicator</c>. Backend compares it against the stored
   * successIndicator, then confirms CAPTURED status with MPGS before
   * writing the iPOS Payment row.
   */
  verifyMagnatiCompletion(
    publicToken: string,
    paymentIntentId: string,
    resultIndicator: string,
  ): Observable<{ ok: boolean; status?: string; message: string }> {
    return this.http
      .post<{ success: boolean; data?: PaymentIntentStatusResponse; error?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/payment-intents/${encodeURIComponent(paymentIntentId)}/magnati/verify-completion`,
        { resultIndicator })
      .pipe(
        map(res => res.success && res.data
          ? { ok: true, status: res.data.status, message: res.data.message }
          : { ok: false, message: res.error ?? 'Verification failed.' }),
        catchError((err: HttpErrorResponse) =>
          of({ ok: false, message: err.error?.error ?? 'Verification failed.' })));
  }

  /**
   * Phase TC-17 — create a Stripe Checkout session for a pickup order.
   *
   * Mirrors {@link createPaymentIntent} but routed under the pickup
   * sub-resource. Error mapping covers the TC-17 backend codes:
   *   - 409 already_paid          → 'pay-not-available' with backend msg
   *   - 409 order_not_payable     → 'no-active-bill' with backend msg
   *   - 409 payment_not_configured → 'pay-not-available' with backend msg
   *   - 409 idempotency_mismatch  → 'idempotency-mismatch'
   *   - 404                       → 'not-available' (anti-enum)
   *   - 502/503                   → 'gateway-error'
   */
  createPickupPaymentIntent(
    publicToken: string,
    orderReference: string,
    request: CreatePickupPaymentIntentRequest,
  ): Observable<CreatePaymentIntentResult> {
    return this.http
      .post<{ success: boolean; data?: CreatePaymentIntentResponse; error?: string; code?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/pickup/${encodeURIComponent(orderReference)}/payment-intents`,
        request)
      .pipe(
        map(res => {
          if (res.success && res.data)
            return { ok: true, data: res.data } as CreatePaymentIntentResult;
          return { ok: false, kind: 'unknown', message: res?.error ?? 'Could not start payment.' } as CreatePaymentIntentResult;
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 409) {
            const code = (err.error?.code as string) ?? '';
            if (code === 'idempotency_mismatch') return of({ ok: false, kind: 'idempotency-mismatch',
              message: err.error?.error ?? 'Please retry the payment.' } as CreatePaymentIntentResult);
            if (code === 'already_paid') return of({ ok: false, kind: 'pay-not-available',
              message: err.error?.error ?? 'This order has already been paid.' } as CreatePaymentIntentResult);
            if (code === 'payment_not_configured') return of({ ok: false, kind: 'pay-not-available',
              message: err.error?.error ?? 'Online payment is not available right now.' } as CreatePaymentIntentResult);
            if (code === 'order_not_payable') return of({ ok: false, kind: 'no-active-bill',
              message: err.error?.error ?? 'This order is not payable.' } as CreatePaymentIntentResult);
            return of({ ok: false, kind: 'no-active-bill',
              message: err.error?.error ?? 'This order is not payable right now.' } as CreatePaymentIntentResult);
          }
          if (err.status === 403)
            return of({ ok: false, kind: 'pay-not-available',
              message: err.error?.error ?? 'Online payment is not available right now.' } as CreatePaymentIntentResult);
          if (err.status === 502 || err.status === 503)
            return of({ ok: false, kind: 'gateway-error',
              message: err.error?.error ?? 'Payment provider unavailable.' } as CreatePaymentIntentResult);
          if (err.status === 404)
            return of({ ok: false, kind: 'not-available',
              message: 'This QR is no longer available.' } as CreatePaymentIntentResult);
          if (err.status === 422)
            return of({ ok: false, kind: 'validation',
              message: err.error?.error ?? 'Please try again.' } as CreatePaymentIntentResult);
          if (err.status >= 400 && err.status < 500)
            return of({ ok: false, kind: 'unknown',
              message: err.error?.error ?? 'Please try again.' } as CreatePaymentIntentResult);
          return of({ ok: false, kind: 'network', message: 'Network error. Please try again.' } as CreatePaymentIntentResult);
        }));
  }

  /**
   * Phase TC-17B — session-keyed payment intent for the pickup
   * "Online" pay mode. The order does not exist yet; the BE server-
   * prices the cart, persists a snapshot, and only after the gateway
   * confirms payment does the order get created. Error mapping
   * mirrors {@link createPickupPaymentIntent}.
   */
  createSessionPaymentIntent(
    publicToken: string,
    sessionId: string,
    request: CreateSessionPaymentIntentRequest,
  ): Observable<CreatePaymentIntentResult> {
    return this.http
      .post<{ success: boolean; data?: CreatePaymentIntentResponse; error?: string; code?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/sessions/${encodeURIComponent(sessionId)}/payment-intents`,
        request)
      .pipe(
        map(res => {
          if (res.success && res.data)
            return { ok: true, data: res.data } as CreatePaymentIntentResult;
          return { ok: false, kind: 'unknown', message: res?.error ?? 'Could not start payment.' } as CreatePaymentIntentResult;
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 409) {
            const code = (err.error?.code as string) ?? '';
            if (code === 'idempotency_mismatch') return of({ ok: false, kind: 'idempotency-mismatch',
              message: err.error?.error ?? 'Please retry the payment.' } as CreatePaymentIntentResult);
            if (code === 'items_unavailable') return of({ ok: false, kind: 'no-active-bill',
              message: err.error?.error ?? 'One or more items are no longer available. Please refresh the menu.' } as CreatePaymentIntentResult);
            if (code === 'payment_not_configured') return of({ ok: false, kind: 'pay-not-available',
              message: err.error?.error ?? 'Online payment is not available right now.' } as CreatePaymentIntentResult);
            return of({ ok: false, kind: 'no-active-bill',
              message: err.error?.error ?? 'This order is not payable right now.' } as CreatePaymentIntentResult);
          }
          if (err.status === 403)
            return of({ ok: false, kind: 'pay-not-available',
              message: err.error?.error ?? 'Online payment is not available right now.' } as CreatePaymentIntentResult);
          if (err.status === 502 || err.status === 503)
            return of({ ok: false, kind: 'gateway-error',
              message: err.error?.error ?? 'Payment provider unavailable.' } as CreatePaymentIntentResult);
          if (err.status === 404)
            return of({ ok: false, kind: 'not-available',
              message: 'This QR is no longer available.' } as CreatePaymentIntentResult);
          if (err.status === 422)
            return of({ ok: false, kind: 'validation',
              message: err.error?.error ?? 'Please try again.' } as CreatePaymentIntentResult);
          if (err.status >= 400 && err.status < 500)
            return of({ ok: false, kind: 'unknown',
              message: err.error?.error ?? 'Please try again.' } as CreatePaymentIntentResult);
          return of({ ok: false, kind: 'network', message: 'Network error. Please try again.' } as CreatePaymentIntentResult);
        }));
  }

  /**
   * Customer-safe poll target for the /payment-result page.
   * Returns the same envelope as the other guest endpoints so the
   * Result page can use the existing classify() error handling.
   */
  getPaymentIntentStatus(
    publicToken: string,
    paymentIntentId: string,
  ): Observable<GuestApiResult<PaymentIntentStatusResponse>> {
    return this.http
      .get<{ success: boolean; data?: PaymentIntentStatusResponse; error?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/payment-intents/${encodeURIComponent(paymentIntentId)}/status`)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestApiResult<PaymentIntentStatusResponse>)
          : ({ ok: false, kind: 'not-available' } as GuestApiResult<PaymentIntentStatusResponse>)),
        catchError((err: HttpErrorResponse) => of(this.classify<PaymentIntentStatusResponse>(err))));
  }

  /**
   * Phase TC-10 — fetch the customer-safe digital receipt.
   *
   * The backend returns 200 with discriminated `available` /
   * `expired` flags for every reasonable failure mode (missing
   * intent, non-Paid check, expired) so the UI does not need to
   * branch on HTTP status. 404 still signals master-switch-off
   * or a wrong/revoked token.
   */
  getReceipt(
    publicToken: string,
    paymentIntentId: string,
  ): Observable<GuestApiResult<DigitalReceiptResponse>> {
    return this.http
      .get<{ success: boolean; data?: DigitalReceiptResponse; error?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/receipts/${encodeURIComponent(paymentIntentId)}`)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestApiResult<DigitalReceiptResponse>)
          : ({ ok: false, kind: 'not-available' } as GuestApiResult<DigitalReceiptResponse>)),
        catchError((err: HttpErrorResponse) => of(this.classify<DigitalReceiptResponse>(err))));
  }

  /**
   * Phase TC-14C — public, polling-only pickup-order status. Backend
   * always returns 200; the response envelope's `data.available` flag
   * tells the page whether to show the status badge or the friendly
   * unavailable state. Network / 5xx errors collapse to a `network`
   * GuestApiResult so the page can show a "Try again" affordance.
   */
  getPickupOrderStatus(
    publicToken: string,
    orderReference: string,
  ): Observable<GuestApiResult<PickupOrderStatusResponse>> {
    return this.http
      .get<{ success: boolean; data?: PickupOrderStatusResponse; error?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/pickup/${encodeURIComponent(orderReference)}/status`)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestApiResult<PickupOrderStatusResponse>)
          : ({ ok: false, kind: 'not-available' } as GuestApiResult<PickupOrderStatusResponse>)),
        catchError((err: HttpErrorResponse) => of(this.classify<PickupOrderStatusResponse>(err))));
  }

  // ─── Phase MOA-2B — shared guest session/cart endpoints ──────
  // Sits beside the legacy TC-4 `submitOrder()` direct path above.
  // GuestCartService now talks through these methods so Browse mode
  // (today) and future Chat mode (MOA-3) share one server-authoritative
  // cart row. All endpoints under /api/public/table-access/{token}/...
  // are anonymous; the publicToken is the only credential.

  createGuestSession(publicToken: string, mode: 'Browse' | 'Chat' = 'Browse')
    : Observable<GuestSessionApiResult> {
    return this.http
      .post<any>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/sessions`,
        { mode })
      .pipe(
        map(res => res?.success && res?.data
          ? ({ ok: true, data: res.data as GuestSessionSnapshot } as GuestSessionApiResult)
          : ({ ok: false, kind: 'unknown', message: res?.error ?? 'Could not start ordering session.' } as GuestSessionApiResult)),
        catchError((err: HttpErrorResponse) => of(this.classifyCart(err))));
  }

  getGuestSession(publicToken: string, sessionId: string): Observable<GuestSessionApiResult> {
    return this.http
      .get<any>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/sessions/${encodeURIComponent(sessionId)}`)
      .pipe(
        map(res => res?.success && res?.data
          ? ({ ok: true, data: res.data as GuestSessionSnapshot } as GuestSessionApiResult)
          : ({ ok: false, kind: 'not-available', message: 'Ordering session unavailable.' } as GuestSessionApiResult)),
        catchError((err: HttpErrorResponse) => of(this.classifyCart(err))));
  }

  addCartLine(publicToken: string, sessionId: string, body: AddGuestCartLineRequest)
    : Observable<GuestSessionApiResult> {
    return this.http
      .post<any>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/sessions/${encodeURIComponent(sessionId)}/cart/lines`,
        body)
      .pipe(
        map(res => res?.success && res?.data
          ? ({ ok: true, data: res.data as GuestSessionSnapshot } as GuestSessionApiResult)
          : ({ ok: false, kind: 'unknown', message: res?.error ?? 'Could not add this item.' } as GuestSessionApiResult)),
        catchError((err: HttpErrorResponse) => of(this.classifyCart(err))));
  }

  updateCartLine(publicToken: string, sessionId: string, lineId: string, body: UpdateGuestCartLineRequest)
    : Observable<GuestSessionApiResult> {
    return this.http
      .put<any>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/sessions/${encodeURIComponent(sessionId)}/cart/lines/${encodeURIComponent(lineId)}`,
        body)
      .pipe(
        map(res => res?.success && res?.data
          ? ({ ok: true, data: res.data as GuestSessionSnapshot } as GuestSessionApiResult)
          : ({ ok: false, kind: 'unknown', message: res?.error ?? 'Could not update this item.' } as GuestSessionApiResult)),
        catchError((err: HttpErrorResponse) => of(this.classifyCart(err))));
  }

  removeCartLine(publicToken: string, sessionId: string, lineId: string)
    : Observable<GuestSessionApiResult> {
    return this.http
      .delete<any>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/sessions/${encodeURIComponent(sessionId)}/cart/lines/${encodeURIComponent(lineId)}`)
      .pipe(
        map(res => res?.success && res?.data
          ? ({ ok: true, data: res.data as GuestSessionSnapshot } as GuestSessionApiResult)
          : ({ ok: false, kind: 'unknown', message: res?.error ?? 'Could not remove this item.' } as GuestSessionApiResult)),
        catchError((err: HttpErrorResponse) => of(this.classifyCart(err))));
  }

  submitGuestSession(publicToken: string, sessionId: string, body: SubmitGuestSessionRequest)
    : Observable<SubmitOrderResult> {
    return this.http
      .post<any>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/sessions/${encodeURIComponent(sessionId)}/submit`,
        body)
      .pipe(
        map(res => res?.success && res?.data
          ? ({ ok: true, data: res.data as SubmitGuestOrderResponse } as SubmitOrderResult)
          : ({ ok: false, kind: 'unknown', message: res?.error ?? 'Could not submit your order.' } as SubmitOrderResult)),
        catchError((err: HttpErrorResponse) => {
          // Map session/cart-specific codes onto the same discriminated
          // union TC-4 already uses, so the cart page's existing error
          // branches keep working.
          if (err.status === 429) return of({ ok: false, kind: 'rate-limited',
            message: 'Too many submits. Please wait a moment and try again.' } as SubmitOrderResult);
          if (err.status === 409) {
            const code = (err.error?.code as string) ?? '';
            if (code === 'items_unavailable' || code === 'item_unavailable') return of({ ok: false, kind: 'items-unavailable',
              message: err.error?.error ?? 'Some items are no longer available. Please refresh the menu.' } as SubmitOrderResult);
            if (code === 'idempotency_fingerprint_mismatch') return of({ ok: false, kind: 'fingerprint-mismatch',
              message: err.error?.error ?? 'Cart changed mid-submit. Please retry.' } as SubmitOrderResult);
            if (code === 'cart_empty') return of({ ok: false, kind: 'validation',
              message: 'Cart is empty.' } as SubmitOrderResult);
            if (code === 'session_terminal') return of({ ok: false, kind: 'not-available',
              message: 'This ordering session is no longer available.' } as SubmitOrderResult);
            return of({ ok: false, kind: 'conflict', message: err.error?.error ?? 'Conflict.' } as SubmitOrderResult);
          }
          if (err.status === 403) return of({ ok: false, kind: 'validation',
            message: err.error?.error ?? 'Ordering is currently disabled for this store.' } as SubmitOrderResult);
          if (err.status === 404) return of({ ok: false, kind: 'not-available',
            message: 'This QR is no longer available.' } as SubmitOrderResult);
          if (err.status >= 400 && err.status < 500) return of({ ok: false, kind: 'validation',
            message: err.error?.error ?? 'Please check your order and try again.' } as SubmitOrderResult);
          return of({ ok: false, kind: 'network', message: 'Network error. Please try again.' } as SubmitOrderResult);
        }));
  }

  // ─── Phase MOA-3C — public guest chat (Meymoona) ───
  // Thin wrapper around the MOA-3A endpoint. Endpoint, persistence,
  // tools, budget guard, throttle, and ledger are already live on
  // staging — this method just types the request and projects errors
  // into the same discriminated-union pattern the rest of the public
  // surface uses, so the chat panel can branch cleanly on `kind`.
  postChatMessage(
    publicToken: string, sessionId: string,
    body: PostGuestChatRequest,
  ): Observable<GuestChatApiResult> {
    return this.http
      .post<{ success: boolean; data?: GuestChatTurnResponse; error?: string; code?: string }>(
        `${this.baseUrl}/public/table-access/${encodeURIComponent(publicToken)}/sessions/${encodeURIComponent(sessionId)}/chat`,
        body)
      .pipe(
        map(res => res.success && res.data
          ? ({ ok: true, data: res.data } as GuestChatApiResult)
          : ({ ok: false, kind: 'unknown',
              message: res?.error ?? 'Could not reach the smart ordering assistant.' } as GuestChatApiResult)),
        catchError((err: HttpErrorResponse) => {
          // 429 — throttled. Surface Retry-After if present so the
          // chat panel can show a precise wait time.
          if (err.status === 429) {
            const retry = err.headers?.get?.('Retry-After') ?? '';
            return of({ ok: false, kind: 'rate-limited',
              message: 'Wait a moment before sending another message.',
              retryAfterSeconds: retry ? parseInt(retry, 10) || undefined : undefined,
            } as GuestChatApiResult);
          }
          if (err.status === 409) {
            const code = (err.error?.code as string) ?? '';
            if (code === 'session_terminal') return of({ ok: false, kind: 'session-terminal',
              message: 'This ordering session is closed. Please scan the QR again.' } as GuestChatApiResult);
            return of({ ok: false, kind: 'conflict',
              message: err.error?.error ?? 'Conflict — please refresh.' } as GuestChatApiResult);
          }
          if (err.status === 404) return of({ ok: false, kind: 'not-available',
            message: 'This QR is no longer available.' } as GuestChatApiResult);
          if (err.status === 400) return of({ ok: false, kind: 'validation',
            message: err.error?.error ?? 'Please rephrase your message.' } as GuestChatApiResult);
          return of({ ok: false, kind: 'network',
            message: 'Network error. Please try again.' } as GuestChatApiResult);
        }));
  }

  /** Same shape as classify() but maps the MOA-2A item/session error codes. */
  private classifyCart(err: HttpErrorResponse): GuestSessionApiResult {
    if (err.status === 429) return { ok: false, kind: 'rate-limited',
      message: 'Too many requests. Please wait a moment and try again.' };
    if (err.status === 409) {
      const code = (err.error?.code as string) ?? '';
      const msg = (err.error?.error as string) ?? 'Please refresh and try again.';
      if (code === 'item_unavailable' || code === 'items_unavailable')
        return { ok: false, kind: 'item-unavailable', message: msg };
      if (code === 'cart_full')        return { ok: false, kind: 'cart-full', message: msg };
      if (code === 'cart_empty')       return { ok: false, kind: 'cart-empty', message: msg };
      if (code === 'session_terminal') return { ok: false, kind: 'session-terminal', message: msg };
      return { ok: false, kind: 'conflict', message: msg };
    }
    if (err.status === 404) return { ok: false, kind: 'not-available',
      message: 'This ordering session is no longer available.' };
    if (err.status >= 400 && err.status < 500) return { ok: false, kind: 'validation',
      message: err.error?.error ?? 'Please check your input and try again.' };
    return { ok: false, kind: 'network', message: 'Network error. Please try again.' };
  }
}
